import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import React from 'react';
import { Currency } from '../../types/account';
import { parseAmountWithCurrency } from '../../utils/helpers';
import AlertDialog from '../AlertDialog';
import { getInsuranceLogo } from './helpers';
import { LoanRequestInsurance, shouldShowMonthlyInsuranceCost } from './types';

type AvailableInsurancesType = {
  hideIcons?: boolean;
  displayFlex?: boolean;
  hideFooter?: boolean;
  hideTitle?: boolean;
  iconSize?: 'small' | 'medium';
  insuranceCost?: number;
  loanRequestInsurances: LoanRequestInsurance[];
  display?: 'inline' | 'block';
  currency: Currency;
  pageCall?: {
    name: string;
    category: string;
  };
  detailsCustomIcon?: React.ReactElement;
  periods: number;
};

export default function LoanRequestInsurancesPreview(
  props: AvailableInsurancesType,
) {
  const {
    hideIcons = false,
    hideFooter = false,
    hideTitle = false,
    iconSize = 'medium',
    insuranceCost,
    display = 'block',
    loanRequestInsurances,
    currency,
    pageCall,
    detailsCustomIcon,
    displayFlex = false,
    periods,
  } = props;

  const hasInsurances = loanRequestInsurances.length > 0;

  return (
    <Box
      style={displayFlex ? {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        width: '100%',
        columnGap: 10,
        alignItems: 'center',
        padding: hideIcons ? '0px' : '5px',
      } : {
        padding: hideIcons ? '0px' : '0.5px',
        borderRadius: 10,
      }}
    >
      <div style={{
        borderRadius: 10,
        padding: hideIcons ? '0px' : '0.5px',
      }}
      >
        {!hideTitle && (
        <Typography
          variant="body2"
          component="div"
          style={{ marginTop: 15 }}
          fontWeight="bold"
        >
          <u>Seguros asociados</u>
        </Typography>
        )}
        <div
          style={
          display === 'inline'
            ? {
              display: 'grid',
              gridTemplateColumns: '3fr 1fr',
              marginTop: 10,
              maxWidth: 450,
              placeContent: 'center',
            }
            : undefined
        }
        >
          <div>
            {!hideIcons
          && (
            <div
              style={{
                display: 'flex',
                columnGap: 10,
                justifyContent: 'center',
                marginBlock: 10,
              }}
            >
              {hasInsurances ? (
                loanRequestInsurances.map((loanRequestInsurance) => getInsuranceLogo({
                  value: loanRequestInsurance.insurance.type,
                  label: loanRequestInsurance.insurance.label,
                  invertColors: true,
                  iconSize,
                }))
              ) : (
                <Typography fontStyle="italic" color="GrayText" height={30}>
                  (Ninguno)
                </Typography>
              )}
            </div>
          )}
            {!hideFooter && (
            <Typography
              variant="caption"
              margin="auto"
              color="GrayText"
              maxWidth={220}
              component="div"
              marginBottom={1}
            >
              {insuranceCost === undefined
                ? 'Al sobreponer el cursor aparecerán los nombres de los seguros asociados'
                : `(+ ${parseAmountWithCurrency(currency, insuranceCost)} )`}
            </Typography>
            )}
          </div>
          <div style={{ height: hideIcons ? '14px' : undefined }}>
            <AlertDialog
              hideLogo
              pageCall={pageCall}
              dialogTitle={(
                <Typography variant="h6" fontWeight="bold" textAlign="center">
                  Detalles de seguros asociados
                </Typography>
              )}
              hideSubmitButton
              button={
            hasInsurances ? (
              detailsCustomIcon || <Button variant="outlined">Ver detalles</Button>
            ) : (
              <div />
            )
          }
              dialogContent={(
                <>
                  <List
                    style={{
                      marginTop: 10,
                      padding: 5,
                      borderRadius: 15,
                    }}
                  >
                    <ListItem
                      secondaryAction={(
                        <Typography width={52} component="div">
                          Valor
                        </Typography>
                  )}
                    >
                      <ListItemText
                        primary={(
                          <Typography marginLeft={10} component="div">
                            Concepto
                          </Typography>
                    )}
                      />
                    </ListItem>
                    <Divider />
                    {loanRequestInsurances.map((lrinsurance) => (
                      <div key={lrinsurance.id}>
                        <ListItem
                          secondaryAction={(
                            <FormControlLabel
                              value="top"
                              control={<div style={{ width: 20, height: 20 }} />}
                              label={(
                                <Typography
                                  variant="caption"
                                  fontWeight="bold"
                                  component="div"
                                  paddingTop={1}
                                >
                                  {parseAmountWithCurrency(
                                    {
                                      abbreviation: lrinsurance.value.currency,
                                      precision: lrinsurance.value.precision,
                                    },
                                    (lrinsurance.value.amount
                                  * 10 ** -lrinsurance.value.precision)
                                      / (shouldShowMonthlyInsuranceCost(lrinsurance.insurance)
                                        ? periods : 1),
                                    false,
                                  )}
                                  {shouldShowMonthlyInsuranceCost(
                                    lrinsurance.insurance,
                                  ) && (
                                  <Typography
                                    variant="caption"
                                    fontStyle="italic"
                                    component="div"
                                    style={{ opacity: 0.7 }}
                                  >
                                    mensuales
                                  </Typography>
                                  )}
                                </Typography>
                          )}
                              labelPlacement="start"
                            />
                      )}
                        >
                          <ListItemAvatar style={{ minWidth: 0, width: 40 }}>
                            {getInsuranceLogo({
                              value: lrinsurance.insurance.type,
                              label: lrinsurance.insurance.label,
                              iconSize: 'medium',
                            })}
                          </ListItemAvatar>
                          <ListItemText
                            style={{ width: 200 }}
                            primary={(
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                component="div"
                              >
                                {lrinsurance.insurance.label}
                              </Typography>
                        )}
                            secondary={(
                              <Typography
                                variant="caption"
                                component="div"
                                sx={{ maxWidth: { xs: 150, sm: 200 } }}
                                textAlign="justify"
                                color="GrayText"
                                fontStyle="italic"
                              >
                                {lrinsurance.insurance.description}
                              </Typography>
                        )}
                          />
                        </ListItem>
                        <Divider />
                      </div>
                    ))}
                  </List>
                  <Typography variant="caption" component="div">
                    El valor presentado es el valor que se agrega al monto total del
                    crédito
                  </Typography>
                </>
          )}
            />
          </div>
        </div>
      </div>
    </Box>
  );
}

LoanRequestInsurancesPreview.defaultProps = {
  hideIcons: false,
  displayFlex: false,
  hideFooter: false,
  hideTitle: false,
  iconSize: 'medium',
  insuranceCost: undefined,
  pageCall: undefined,
  detailsCustomIcon: undefined,
  display: 'block',
};
